import AppHeader from '@components/AppNavigation/AppHeader';
import TextButton from '@components/Buttons/TextButton';
import FlowStatusChip, { FlowStatus } from '@components/Flows/FlowStatusChip';
import Loader from '@components/Loader/Loader';
import { ContentCopy } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Button, Link as MUILink, Tooltip, Typography } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { APP_ROUTES } from 'constants/routes';
import { useToast } from 'hooks';
import React, { ReactElement, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ITheme } from 'types/theme';
import { ToastSeverity } from 'types/toast';
import { useCopyToClipboard } from 'usehooks-ts';
import FlowBuilderEditableTitle from './FlowBuilderEditableTitle';

interface IFlowBuilderHeaderProps {
	formik: any;
	onCancel: () => void;
	onSave: () => void;
	isEditingScreenTask?: boolean;
	flowId?: string;
	disabled?: boolean;
	isLoading?: boolean;
	isDirty?: boolean;
	flowName?: string;
	buttons?: ReactElement;
}

const FlowBuilderHeader = ({
	isEditingScreenTask,
	isLoading,
	flowName,
	disabled,
	flowId,
	formik,
	isDirty,
	onSave,
	onCancel,
	buttons
}: IFlowBuilderHeaderProps) => {
	const theme = useTheme<ITheme>();
	const { addToast } = useToast();
	const [, copyFn] = useCopyToClipboard();

	const copyFunc = useCallback(async () => {
		const copied = await copyFn(flowId as string);
		if (copied) {
			addToast({
				severity: ToastSeverity.Success,
				content: intl.tr('toast.value.copied.to.clipboard')
			});
		}
	}, [copyFn, flowId, addToast]);

	return (
		<Box
			display="flex"
			flexDirection="row"
			bgcolor={theme.palette.background.default}
			height={70}
		>
			<Link to={`${APP_ROUTES.flows}`} data-testid="flows-builder-back">
				<AppHeader translation="" sx={{ px: 1, pl: 2 }}>
					<Box display="flex" flexDirection="row">
						<MUILink sx={{ textDecoration: 'none' }} component="span">
							{intl.tr('pages.flows.title')}
						</MUILink>
						<Box pl={2}>/</Box>
					</Box>
				</AppHeader>
			</Link>
			<Box display="flex">
				<Loader variant="header" isLoading={!!isLoading}>
					{!isEditingScreenTask ? (
						<Box display="flex" flexDirection="row" alignItems="center">
							<FlowBuilderEditableTitle formik={formik} autoFocus={!flowName} />
						</Box>
					) : (
						<>
							<TextButton
								onClick={onCancel}
								sx={{ p: 0, textDecoration: 'none', mx: 0 }}
								data-testid="screen-builder-back"
							>
								<AppHeader
									label={flowName}
									sx={{
										m: 0,
										px: 0
									}}
								/>
							</TextButton>
							<AppHeader label="/" sx={{ mx: 1, px: 1 }} />
							<Box display="flex" flexDirection="row" alignItems="center">
								<FlowBuilderEditableTitle formik={formik} autoFocus />
							</Box>
						</>
					)}
				</Loader>
			</Box>
			{!isEditingScreenTask && flowId && (
				<Box
					display="flex"
					flexDirection="row"
					justifyContent="center"
					alignItems="center"
				>
					<Typography variant="body1" ml={2} mr={1}>
						{intl.tr('flows.builder.id')}: {flowId}
					</Typography>
					<Button
						size="small"
						color="primary"
						onClick={copyFunc}
						aria-label={intl.tr('flows.copy.flowId.tooltip')}
						sx={{
							padding: 0,
							minHeight: 0,
							minWidth: 0
						}}
						data-testid="copy-flow-id-button"
					>
						<Tooltip
							title={intl.tr('flows.copy.flowId.tooltip')}
							placement="bottom"
						>
							<ContentCopy fontSize="small" />
						</Tooltip>
					</Button>
				</Box>
			)}
			{!isEditingScreenTask && disabled !== undefined && (
				<Box px={1} alignItems="center" justifyContent="center" display="flex">
					<FlowStatusChip
						status={disabled ? FlowStatus.disabled : FlowStatus.active}
					/>
				</Box>
			)}
			<Box
				flexGrow={1}
				pr={4}
				pl={2}
				alignItems="center"
				justifyContent="flex-end"
				display="flex"
			>
				{buttons}
				<LoadingButton
					variant={isEditingScreenTask ? 'contained' : 'outlined'}
					sx={{ ml: 1 }}
					color="primary"
					disabled={!isEditingScreenTask && !isDirty}
					loading={formik.isSubmitting}
					onClick={onSave}
					data-testid="flow-save-button"
				>
					{isEditingScreenTask
						? intl.tr('button.done')
						: intl.tr('button.save')}
				</LoadingButton>
			</Box>
		</Box>
	);
};

FlowBuilderHeader.defaultProps = {
	flowName: '',
	flowId: undefined,
	isLoading: false,
	isEditingScreenTask: false,
	isDirty: false,
	disabled: undefined,
	buttons: undefined
};

export default FlowBuilderHeader;
